import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Loader2, ArrowLeft, Lock, UserPlus, CheckCircle, RefreshCw, Ticket, Smartphone, AlertTriangle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ReportBugLink from "../components/ReportBugLink";
import RegisterForm from "../components/RegisterForm";

const HomePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [activeView, setActiveView] = useState(null); // null, "login", "register"

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = { ...loginData };
      if (requires2FA && totpCode) {
        payload.totp_code = totpCode;
      }
      
      const response = await axios.post(`${API}/auth/login`, payload);
      
      // Admin requires 2FA setup
      if (response.data.requires_2fa_setup) {
        toast.warning("Configuration 2FA requise pour les administrateurs");
        // Store temp token and initiate 2FA setup
        const tempToken = response.data.temp_token;
        try {
          const setupResponse = await axios.post(`${API}/auth/2fa/setup`, {}, {
            headers: { Authorization: `Bearer ${tempToken}` }
          });
          setSetup2FAData({
            ...setupResponse.data,
            tempToken: tempToken
          });
          setRequires2FASetup(true);
        } catch (setupError) {
          toast.error("Erreur lors de la configuration 2FA");
        }
        setIsLoading(false);
        return;
      }
      
      if (response.data.requires_2fa && !response.data.access_token) {
        setRequires2FA(true);
        toast.info("Veuillez entrer votre code 2FA");
        setIsLoading(false);
        return;
      }
      
      localStorage.setItem("token", response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem("refreshToken", response.data.refresh_token);
      }
      
      setUser(response.data.user);
      setRequires2FA(false);
      setTotpCode("");
      toast.success(t('auth.welcomeBack'));
      
      if (response.data.user.role === "super_admin") {
        navigate("/super-admin");
      } else if (response.data.user.role === "admin") {
        navigate("/admin");
      } else if (response.data.user.role === "owner") {
        navigate("/business");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      if (error.response?.data?.detail === "Code 2FA invalide") {
        toast.error("Code 2FA invalide. Veuillez réessayer.");
        setTotpCode("");
      } else {
        toast.error(error.response?.data?.detail || "Login failed");
        setRequires2FA(false);
      }
    }
    setIsLoading(false);
  };

  // Handle 2FA setup verification for admins
  const handleVerify2FASetup = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error("Veuillez entrer un code à 6 chiffres");
      return;
    }
    
    setIsLoading(true);
    try {
      // Verify the 2FA setup
      await axios.post(`${API}/auth/2fa/verify`, 
        { totp_code: totpCode },
        { headers: { Authorization: `Bearer ${setup2FAData.tempToken}` }}
      );
      
      toast.success("2FA activé avec succès!");
      
      // Now login again with 2FA
      setRequires2FASetup(false);
      setSetup2FAData(null);
      setRequires2FA(true);
      setTotpCode("");
      toast.info("Veuillez maintenant vous reconnecter avec votre code 2FA");
    } catch (error) {
      toast.error("Code invalide. Veuillez réessayer.");
      setTotpCode("");
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success("Un email de réinitialisation a été envoyé à votre adresse.");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de l'email");
    }
    setIsLoading(false);
  };

  // Redirect logged-in users to their dashboard
  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") {
        navigate("/super-admin");
      } else if (user.role === "admin") {
        navigate("/admin");
      } else if (user.role === "owner") {
        navigate("/business");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  // Show nothing while redirecting
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2eb382] mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-['Open_Sans']">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Black & White Concert Image */}
        <img 
          src="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1920&q=80" 
          alt="Concert atmosphere"
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-gray-900/60 to-black/80"></div>
        
        {/* Content over image */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo */}
          <div>
            <Link to="/" className="inline-block">
              <img src="/logo-white-hr-cropped.png" alt="Middleman" className="h-10" />
            </Link>
          </div>
          
          {/* Center Content */}
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight">
              Transférez vos billets<br />en toute sécurité
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              La plateforme de confiance pour acheter et vendre des billets d'événements entre particuliers.
            </p>
            
            {/* Trust badges - Updated text */}
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Shield className="h-4 w-4 text-[#67ffbb]" />
                <span className="text-sm">Vous êtes protégé</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <CheckCircle className="h-4 w-4 text-[#67ffbb]" />
                <span className="text-sm">Billets et paiements validés</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Lock className="h-4 w-4 text-[#67ffbb]" />
                <span className="text-sm">Échange sécurisé</span>
              </div>
            </div>
          </div>
          
          {/* Bottom stats - Updated text */}
          <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
            <div>
              <p className="text-2xl font-bold text-white/60">Transfert</p>
              <p className="text-sm text-[#67ffbb] font-semibold">Simple</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white/60">Échange</p>
              <p className="text-sm text-[#67ffbb] font-semibold">Rapide</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white/60">En toute</p>
              <p className="text-sm text-[#67ffbb] font-semibold">Sécurité</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <img src="/logo-dark-cropped.png" alt="Middleman" className="h-5 max-w-[120px] object-contain" />
          <LanguageSwitcher />
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between p-6">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Retour au site</span>
          </Link>
          <LanguageSwitcher />
        </div>

        {/* Auth Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.welcome')}</h1>
              <p className="text-gray-500">{t('home.subtitle')}</p>
            </div>

            {/* Desktop Title */}
            <div className="hidden lg:block text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.welcome')}</h2>
              <p className="text-gray-500">{t('home.subtitle')}</p>
            </div>

            {/* Auth Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              {activeView === null && (
                <div className="space-y-5">
                  {/* Security Badge */}
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500 pb-2">
                    <Shield className="h-4 w-4 text-[#67ffbb]" />
                    <span>Connexion sécurisée 256-bit SSL</span>
                  </div>

                  <Button
                    onClick={() => setActiveView("login")}
                    className="w-full h-14 text-base bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 rounded-xl font-semibold shadow-lg shadow-[#67ffbb]/20 transition-all hover:shadow-xl hover:shadow-[#67ffbb]/30"
                    data-testid="login-tab"
                  >
                    <Lock className="h-5 w-5 mr-2" />
                    {t('auth.login')}
                  </Button>
                  
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-400">ou</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => setActiveView("register")}
                    variant="outline"
                    className="w-full h-14 text-base border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#67ffbb] rounded-xl font-semibold"
                    data-testid="register-tab"
                  >
                    <UserPlus className="h-5 w-5 mr-2" />
                    {t('auth.register')}
                  </Button>

                  {/* Features Grid - Updated badges */}
                  <div className="hidden sm:block mt-8 pt-6 border-t border-gray-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-[#67ffbb]/20 rounded-xl flex items-center justify-center">
                          <Shield className="h-5 w-5 text-[#2eb382]" />
                        </div>
                        <p className="text-xs text-gray-600 font-medium">Vous êtes protégé</p>
                      </div>
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-[#67ffbb]/20 rounded-xl flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-[#2eb382]" />
                        </div>
                        <p className="text-xs text-gray-600 font-medium">Billets validés</p>
                      </div>
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-[#67ffbb]/20 rounded-xl flex items-center justify-center">
                          <Lock className="h-5 w-5 text-[#2eb382]" />
                        </div>
                        <p className="text-xs text-gray-600 font-medium">Échange sécurisé</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeView === "login" && (
                <div>
                  <button
                    type="button"
                    onClick={() => setActiveView(null)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                  </button>

                  <div className="text-center mb-8">
                    <div className="w-14 h-14 mx-auto bg-[#67ffbb]/20 rounded-2xl flex items-center justify-center mb-4">
                      <Lock className="h-7 w-7 text-[#2eb382]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Connexion</h2>
                    <p className="text-gray-500 mt-1">Accédez à votre compte</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-gray-700 font-medium">{t('auth.email')}</Label>
                      <Input
                        id="login-email"
                        name="email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        autoComplete="email"
                        data-testid="login-email"
                        disabled={requires2FA}
                        className="h-12 rounded-xl border-gray-200 focus:border-[#67ffbb] focus:ring-[#67ffbb]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-gray-700 font-medium">{t('auth.password')}</Label>
                      <Input
                        id="login-password"
                        name="password"
                        type="password"
                        placeholder={t('auth.passwordPlaceholder')}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        autoComplete="current-password"
                        data-testid="login-password"
                        disabled={requires2FA}
                        className="h-12 rounded-xl border-gray-200 focus:border-[#67ffbb] focus:ring-[#67ffbb]"
                      />
                    </div>
                    
                    {requires2FA && (
                      <div className="space-y-2 p-4 bg-[#67ffbb]/10 rounded-xl border border-[#67ffbb]/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-5 w-5 text-[#2eb382]" />
                          <Label htmlFor="totp-code" className="text-[#2eb382] font-medium">
                            Code d'authentification 2FA
                          </Label>
                        </div>
                        <Input
                          id="totp-code"
                          type="text"
                          placeholder="000000"
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="text-center text-2xl tracking-widest font-mono h-14 rounded-xl"
                          maxLength={6}
                          required
                          autoFocus
                        />
                        <p className="text-xs text-[#2eb382] text-center mt-2">
                          Entrez le code à 6 chiffres de votre application
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 rounded-xl font-semibold shadow-lg shadow-[#67ffbb]/20"
                      disabled={isLoading}
                      data-testid="login-submit"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Connexion en cours...
                        </>
                      ) : requires2FA ? (
                        "Vérifier le code 2FA"
                      ) : (
                        "Se connecter"
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="w-full text-sm text-[#2eb382] hover:text-[#269970] hover:underline transition-colors pt-2"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                    
                    {/* Report bug link */}
                    <div className="flex justify-center pt-4 border-t border-gray-100 mt-4">
                      <ReportBugLink variant="muted" />
                    </div>
                  </form>
                </div>
              )}

              {activeView === "register" && (
                <div>
                  <button
                    type="button"
                    onClick={() => setActiveView(null)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('common.back')}
                  </button>

                  <div className="text-center mb-8">
                    <div className="w-14 h-14 mx-auto bg-[#67ffbb]/20 rounded-2xl flex items-center justify-center mb-4">
                      <UserPlus className="h-7 w-7 text-[#2eb382]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('auth.createAccount')}</h2>
                    <p className="text-gray-500 mt-1">{t('auth.joinFree')}</p>
                  </div>

                  <RegisterForm 
                    onSuccess={(userData) => {
                      setUser(userData);
                      navigate("/dashboard");
                    }}
                  />
                </div>
              )}
            </div>

            {/* Terms */}
            <p className="mt-6 text-center text-xs text-gray-400">
              En continuant, vous acceptez nos{" "}
              <Link to="/terms" className="text-[#2eb382] hover:underline">
                Conditions d'utilisation
              </Link>{" "}
              et notre{" "}
              <Link to="/privacy" className="text-[#2eb382] hover:underline">
                Politique de confidentialité
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="py-4 text-center border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-400">
            © 2026 Middleman • Transferts de billets sécurisés
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md rounded-2xl font-['Open_Sans']">
          <DialogHeader>
            <DialogTitle className="text-xl">Réinitialiser votre mot de passe</DialogTitle>
            <DialogDescription>
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Adresse email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="votre@email.com"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                className="h-12 rounded-xl"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1 rounded-xl"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 rounded-xl font-semibold"
                disabled={isLoading}
              >
                {isLoading ? "Envoi..." : "Envoyer le lien"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin 2FA Setup Required Dialog */}
      <Dialog open={requires2FASetup} onOpenChange={(open) => {
        if (!open) {
          setRequires2FASetup(false);
          setSetup2FAData(null);
          setTotpCode("");
        }
      }}>
        <DialogContent className="sm:max-w-lg rounded-2xl font-['Open_Sans']">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Configuration 2FA obligatoire</DialogTitle>
                <DialogDescription>
                  En tant qu'administrateur, vous devez activer l'authentification à deux facteurs.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {setup2FAData && (
            <div className="space-y-6 mt-4">
              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  1. Téléchargez une application d'authentification
                </p>
                <p className="text-xs text-gray-500 ml-4">
                  Google Authenticator, Microsoft Authenticator, ou Authy
                </p>
                <p className="text-sm font-medium text-gray-700">
                  2. Scannez le QR code ci-dessous
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-inner">
                  <img 
                    src={setup2FAData.qr_code} 
                    alt="QR Code 2FA" 
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual code */}
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Ou entrez ce code manuellement :</p>
                <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono tracking-wider">
                  {setup2FAData.secret}
                </code>
              </div>

              {/* Verification form */}
              <form onSubmit={handleVerify2FASetup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="setup-totp" className="text-sm font-medium">
                    3. Entrez le code à 6 chiffres affiché dans l'application
                  </Label>
                  <Input
                    id="setup-totp"
                    type="text"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest font-mono h-14 rounded-xl"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setRequires2FASetup(false);
                      setSetup2FAData(null);
                      setTotpCode("");
                    }}
                    className="flex-1 rounded-xl"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 rounded-xl font-semibold"
                    disabled={isLoading || totpCode.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Vérification...
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-4 w-4" />
                        Activer 2FA
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {/* Backup codes notice */}
              {setup2FAData.backup_codes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    ⚠️ Conservez ces codes de secours en lieu sûr :
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {setup2FAData.backup_codes.map((code, index) => (
                      <code key={index} className="bg-white px-2 py-1 rounded text-xs font-mono text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
